import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, IconButton, InputBase, Link, Typography, useTheme } from '@mui/material';
import React, { useCallback } from 'react';

import { textFieldSx } from './filterBarStyles';

/** The search box and Select All / Clear links at the top of
 *  `MultiSelectDropdown`'s popper. Extracted to keep that component under the
 *  project's 250-line file budget. */
export interface MultiSelectDropdownSearchProps {
  /** Dropdown label, used to build the control's accessible names. */
  label: string;
  search: string;
  /** How many options are ticked. Shown so the count is legible without
   *  scrolling the list to count checkboxes. */
  selectedCount: number;
  onSearchChange: (value: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
}

export const MultiSelectDropdownSearch: React.FC<MultiSelectDropdownSearchProps> = ({
  label,
  search,
  selectedCount,
  onSearchChange,
  onSelectAll,
  onClear,
}) => {
  const theme = useTheme();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const accentDark = theme.palette.v4.accentDark;

  const linkSx = {
    color: accentDark,
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: 600,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  };

  // Clears the option-list search only — ticked options are left alone, which
  // is what the "Clear" link beneath is for.
  const handleClearSearch = useCallback(() => {
    onSearchChange('');
    inputRef.current?.focus();
  }, [onSearchChange]);

  // The popper renders through a portal, but React events still travel up the
  // component tree to FilterBar's Enter-to-search handler. Every button in
  // here needs Enter stopped, or keyboard-activating it would also fire a
  // search against the pre-change state.
  const stopEnter = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') e.stopPropagation();
  }, []);

  return (
    <>
      <Box sx={{ px: 1.25, pt: 1.25, pb: 1 }}>
        <InputBase
          placeholder={`Search ${label.toLowerCase()}`}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          fullWidth
          inputRef={inputRef}
          inputProps={{ 'aria-label': `Search ${label}` }}
          startAdornment={
            <SearchIcon
              sx={{ fontSize: 15, mr: 0.5, color: theme.palette.v4.textFaint }}
              aria-hidden
            />
          }
          endAdornment={
            search ? (
              <IconButton
                size="small"
                onClick={handleClearSearch}
                onKeyDown={stopEnter}
                aria-label={`Clear ${label} search`}
                sx={{ p: 0.25, mr: -0.5, color: theme.palette.text.secondary }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            ) : null
          }
          sx={textFieldSx(theme, search.length > 0)}
        />
      </Box>

      {/* Bulk actions on the left, the running count on the right, closed off
          with a hairline so the option list below reads as its own region. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.25,
          pb: 0.875,
          borderBottom: `1px solid ${theme.palette.v4.border}`,
        }}
      >
        <Link
          component="button"
          onClick={onSelectAll}
          onKeyDown={stopEnter}
          aria-label={`Select all ${label}`}
          sx={linkSx}
        >
          Select all
        </Link>
        <Link
          component="button"
          onClick={onClear}
          onKeyDown={stopEnter}
          aria-label={`Clear ${label}`}
          sx={linkSx}
        >
          Clear
        </Link>
        <Typography
          aria-live="polite"
          sx={{
            ml: 'auto',
            fontSize: '11px',
            color: theme.palette.v4.textFaint,
          }}
        >
          {selectedCount > 0 ? `${selectedCount} selected` : 'None selected'}
        </Typography>
      </Box>
    </>
  );
};
